import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import AvatarEditor from 'react-avatar-editor'

import { useUpdatePicture } from '../../../../../../hooks/profile/useUpdatePicture'
import { useDeletePicture } from '../../../../../../hooks/profile/useDeletePicture'

import Icon from '../../../../../ui/Icon'
import { Button } from '../../../../../buttons/Button'
import FormSection from '../../../../../formSection/FormSection'
import ImageInput from '../../../../../input/imageInput/ImageInput'
import ProfilePhotoPreview from './preview/ProfilePhotoPreview'
import DropZoneInput from '../../../../../input/dragDropInput/DropzoneInput'

import { useApp } from '../../../../../../contexts/AppContext'
import { useProfileContext } from '../../../../../../contexts/ProfileContext'
import { UserProfile } from '../../../../../../types/user/userProfile'
import { BannerKind } from '../../../../../../types/contexts/AppContext'

type ProfilePhotoProps = {
  profile: UserProfile | null
  userEmail: string
}

const MAX_PROFILE_PICTURE_SIZE = 4 * 1024 * 1024

const ProfilePhoto = ({ profile, userEmail }: ProfilePhotoProps) => {
  const { t } = useTranslation('account')
  const { showBanner } = useApp()
  const { pictureUrl: picture, setPicture } = useProfileContext()
  const editorRef = useRef<React.ComponentRef<typeof AvatarEditor>>(null)

  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [zoom, setZoom] = useState(1)

  const {
    updatePicture,
    loading: updateLoading,
    error: updateError,
    success: updateSuccess,
    resetState: resetUpdateState,
  } = useUpdatePicture()

  const {
    deletePicture,
    loading: deleteLoading,
    error: deleteError,
    success: deleteSuccess,
    resetState: resetDeleteState,
  } = useDeletePicture()

  const loading = updateLoading || deleteLoading
  const error = updateError || deleteError
  const success = updateSuccess || deleteSuccess

  useEffect(() => {
    if (error) {
      showBanner({
        kind: BannerKind.ERR,
        msg: error,
      })
    }

    if (success) {
      showBanner({
        kind: BannerKind.OK,
        msg: success,
      })
    }

    resetUpdateState()
    resetDeleteState()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error, success])

  const handleSave = () => {
    const canvas = editorRef.current?.getImageScaledToCanvas()

    if (!canvas) {
      return
    }

    canvas.toBlob(async blob => {
      if (!blob) {
        return
      }

      const file = new File([blob], 'profile.png', {
        type: 'image/png',
      })

      setSelectedFile(null)
      const updated = await updatePicture(file)

      if (updated) {
        setPicture(file)
      }
    }, 'image/png')
  }

  const handleCancel = () => {
    setSelectedFile(null)
    setZoom(1)
  }

  const handleRemove = async () => {
    const deleted = await deletePicture()

    if (deleted) {
      setPicture(null)
      setSelectedFile(null)
      setZoom(1)
    }
  }

  return (
    <FormSection
      title={t('account.profile.photo.title')}
      subtitle={t('account.profile.photo.subtitle')}
      actions={
        !selectedFile && (
          <Button
            label={t('account.profile.photo.remove')}
            icon={<Icon name="trash" size={13} />}
            variant="ghost"
            size="sm"
            block
            className="profile-photo-remove-btn"
            onClick={handleRemove}
            disabled={!picture}
            loading={loading}
          />
        )
      }
    >
      <DropZoneInput
        accept={{
          'image/png': [],
          'image/jpeg': [],
        }}
        maxSize={MAX_PROFILE_PICTURE_SIZE}
        disabled={!!selectedFile || loading}
        onFilesRejected={rejections => {
          const fileIsTooLarge = rejections.some(rejection =>
            rejection.errors.some(error => error.code === 'file-too-large')
          )

          showBanner({
            kind: BannerKind.ERR,
            msg: fileIsTooLarge
              ? t('account.profile.photo.validation.max-size')
              : t('account.profile.photo.validation.invalid-file'),
          })
        }}
        onFilesSelected={([file]) => {
          if (!file) {
            return
          }

          setZoom(1)
          setSelectedFile(file)
        }}
      >
        {selectedFile ? (
          <ImageInput
            ref={editorRef}
            image={selectedFile}
            zoom={zoom}
            onZoomChange={setZoom}
          >
            <Button
              label={t('account.upload-btn')}
              icon={<Icon name="plus" size={13} />}
              variant="dark"
              size="sm"
              onClick={handleSave}
              loading={loading}
            />
            <Button
              label={t('account.cancel-btn')}
              variant="ghost"
              size="sm"
              onClick={handleCancel}
              disabled={loading}
            />
          </ImageInput>
        ) : (
          <>
            <ProfilePhotoPreview
              image={picture}
              firstName={profile?.firstName}
              fallbackName={userEmail}
            />
            <div className="profile-photo-hint">
              <span>{t('account.select-picture')}</span>
              <span>{t('account.drag-drop')}</span>
            </div>
          </>
        )}
      </DropZoneInput>
    </FormSection>
  )
}

export default ProfilePhoto
